import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import { Dialog, Toolbar } from "client/Components";
import {
	AvailableAppModel,
	SpaceAccessKind,
	SpaceInfo,
	SpaceKind
} from "client/Api/Workspace/workspace";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useUnmountedCancellation
} from "client/Hooks";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import { AppColorViewModel } from "client/Api/Apps/common";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { AddAppsAsTabs } from "../AddAppsAsTabs/AddAppsAsTabs";
import { Form, FormHandle } from "./Form";
import {
	getNavigationForEdit,
	GlobalNavigationEditModel,
	GlobalNavigationItemEditModel,
	GlobalNavigationItemKind,
	GlobalNavigationSaveResult,
	saveNavigation,
	GlobalNavigationPermissionsModel,
	AppGlobalNavigationItemDataEditModel,
	SpaceUserInfo
} from "client/Api/TitleBar/GlobalNavigation/globalNavigation";
import {
	ensureNotNil,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { fromArray } from "client_legacy/ndaw/mapHelpers";
import {
	AppGlobalNavigationItemFormModel,
	GlobalNavigationFormModel,
	GlobalNavigationItemFormModel,
	isAppItem,
	isGroupItem
} from "../misc";
import {
	getClientId,
	ObjectWithClientId,
	setClientId
} from "client/Misc/clientId";
import { Guid } from "client_legacy/ndaw/guid";
import { SavedSpace, useEditSpaceDialog } from "client/Features/Workspace/EditSpaceDialog/EditSpaceDialog";
import { useStartCreateSpace } from "client/Features/Workspace/useStartCreateSpace";
import {
	WorkspaceEvent,
	publish as workspacePublish
} from "client/Features/Workspace/events";

export const MAX_SPACE_NAME_LENGTH = 32;

export type GlobalNavigationManagementInitialAction = "create-group";

export type GlobalNavigationManagementProps = {
	initialAction?: GlobalNavigationManagementInitialAction;
	onAfterSaved(): void;
	onClose(): void;
};

const GlobalNavigationManagement = (props: GlobalNavigationManagementProps): ReactElement => {
	const {
		initialAction: initialActionFromProps,
		onAfterSaved,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [addAppsSliderState, setAddAppsSliderState] = useState<AddAppsSliderState>({ isOpen: false });
	const [appItemsInfo, setAppItemsInfo] = useState<Map<number, AppGlobalNavigationItemInfo>>(new Map());
	const [permissions, setPermissions] = useState<GlobalNavigationPermissionsModel>(initialPermissions);
	const [recentlyRemovedAppIds, setRecentlyRemovedAppIds] = useState<readonly number[]>([]);
	const [formData, setFormData] = useState<GlobalNavigationFormModel>(formInitialState);
	const [initialAction] = useState<GlobalNavigationManagementInitialAction | undefined>(initialActionFromProps);
	const [appIdToScroll, setAppIdToScroll] = useState<Guid | null>(null);

	const formRef: RefObject<FormHandle | null> = useRef<FormHandle>(null);

	const formHandler: IFormHandler<GlobalNavigationFormModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const executeInitialAction = useCallback(
		(): void => {
			if (!initialAction) {
				return;
			}

			const form: FormHandle = ensureNotNil(formRef.current);

			switch(initialAction) {
				case "create-group":
					form.createGroup();
					break;
				default:
					never(initialAction);
			}
		},
		[initialAction]
	);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [editSpaceDialog, openEditSpaceDialog] = useEditSpaceDialog();
	const [startCreateSpaceElement, startCreateSpace] = useStartCreateSpace();

	const saveHandler = useCallback(
		async (
			onSaved?: (result: GlobalNavigationSaveResult) => void,
			onValidationError?: () => void
		): Promise<void> => {
			if(isBusy) {
				return;
			}
			try {
				setIsBusy(true);
				const validationResult: InputValidationResult = await formHandler.validation
					.validate(globalCancellation);
				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					onValidationError?.();
					return;
				}

				const result: GlobalNavigationSaveResult = await saveNavigation(
					formData,
					globalCancellation
				);
				setIsBusy(false);

				onSaved?.(result);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				setIsBusy(false);
				if (!formHandler.validation.tryProcessServerError(e)) {
					alertManager.showError(localize("_GeneralErrorMessage"));
					console.error(e);
				}
			}
		},
		[
			alertManager,
			formData,
			formHandler.validation,
			globalCancellation,
			isBusy
		]
	);

	const submitHandler = useCallback(
		(): void =>{
			saveHandler(
				() => {
					onAfterSaved();
					onClose();
				}
			);
		},
		[
			onAfterSaved,
			saveHandler,
			onClose
		]
	);

	const addAppAsTabHandler = useCallback(
		(selectedApps: AvailableAppModel[]): void => {
			const selectedAppsIds: number[] = selectedApps.map(x => x.installedAppId);

			setAppItemsInfo(existingApps => {
				const result: Map<number, AppGlobalNavigationItemInfo> = new Map(existingApps);
				selectedApps.forEach(x => {
					const isAdded: boolean = result.has(x.installedAppId);
					if (!isAdded) {
						result.set(
							x.installedAppId,
							{
								name: x.name,
								image: x.image,
								color: x.color,
							}
						);
					}
				});
				return result;
			});

			setFormData(prevState => {
				const existingIds = new Set<number>(
					getInstalledAppIds(prevState.items)
				);

				const newAppItems: AppGlobalNavigationItemFormModel[] = selectedApps
					.filter(
						a => !existingIds.has(a.installedAppId)
					)
					.map((a): AppGlobalNavigationItemFormModel => {
						return setClientId({
							id: null,
							kind: GlobalNavigationItemKind.InstalledApp,
							appData: {
								installedAppId: a.installedAppId
							}
						});
					});

				if (newAppItems.length > 0) {
					const lastAppId: Guid = getClientId(
						newAppItems[newAppItems.length - 1]
					);

					setAppIdToScroll(lastAppId);
				}

				const targetGroupIndex: number | null = addAppsSliderState.isOpen
					? addAppsSliderState.targetGroupIndex
					: null;

				if (isNil(targetGroupIndex)) {
					return {
						items: [
							...prevState.items,
							...newAppItems
						]
					};
				}

				const targetGroup: GlobalNavigationItemFormModel = prevState.items[targetGroupIndex];

				if (!isGroupItem(targetGroup)) {
					throw new Error("Expected group item at this point");
				}

				const itemsCopy: GlobalNavigationItemFormModel[] = [
					...prevState.items
				];

				const groupChildrenCopy: GlobalNavigationItemFormModel[] = [
					...targetGroup.groupData
						.children
				];

				groupChildrenCopy.push(...newAppItems);

				itemsCopy[targetGroupIndex] = {
					...targetGroup,
					groupData: {
						...targetGroup.groupData,
						children: groupChildrenCopy
					}
				};

				return {
					items: itemsCopy
				};
			});

			setRecentlyRemovedAppIds(
				ids => ids.filter(id => !selectedAppsIds.includes(id))
			);
		},
		[addAppsSliderState]
	);

	useEffect(
		() => {
			async function fetchItems(signal: AbortSignal): Promise<void> {
				setIsBusy(true);
				try {
					const models: GlobalNavigationEditModel = await getNavigationForEdit(signal);
					const newItems: GlobalNavigationItemFormModel[] = models.items
						.map(x => convertToGlobalNavigationItemFormModel(x));

					const newAppItemsInfo: Map<number, AppGlobalNavigationItemInfo> = fromArray(
						getAppItemsData(models.items),
						x => x.data.installedAppId,
						x => ({
							name: x.name,
							image: x.data.image,
							color: x.data.color
						})
					);

					setAppItemsInfo(newAppItemsInfo);

					setPermissions(models.permissions);
					setFormData({
						items: newItems
					});
					setIsBusy(false);
					executeInitialAction();
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
				}
			}

			const cancellation = new AbortController();
			fetchItems(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager, executeInitialAction]
	);

	const createSharedSpaceHandler = useCallback(
		async (): Promise<TryResult<SavedSpace>> => {
			return startCreateSpace(false);
		},
		[startCreateSpace]
	);


	const editSpaceHandler = useCallback(
		async (id: number): Promise<void> => {
			const result: TryResult<SavedSpace> = await openEditSpaceDialog({
				mode: {
					kind: "edit",
					id
				}
			});

			if (!result.wasSuccessful) {
				return;
			}

			const space: SavedSpace = result.result;

			if (space.kind !== SpaceKind.Shared) {
				return;
			}

			const {
				kind,
				name,
				userInfo
			} = space;

			setFormData(
				(x): GlobalNavigationFormModel => updateSpaceInfo(
					x,
					id,
					name,
					userInfo
				)
			);

			const updatedSpace: SpaceInfo = {
				id,
				kind,
				name,
				accessKind: userInfo.canEdit
					? SpaceAccessKind.Edit
					: SpaceAccessKind.Access
			};

			workspacePublish(
				WorkspaceEvent.SpaceUpdated,
				{
					space: updatedSpace
				}
			);
		},
		[openEditSpaceDialog]
	);

	if (addAppsSliderState.isOpen) {
		return (
			<AddAppsAsTabs
				onAdd={addAppAsTabHandler}
				onClose={() => setAddAppsSliderState({ isOpen: false })}
				alreadyInstalledAppIdsThatShouldAlsoBeIncluded={recentlyRemovedAppIds}
			/>
		);
	}

	const hasOtherDialogOpen: boolean = isNotNil(editSpaceDialog)
		|| isNotNil(startCreateSpaceElement);

	return (
		<>
			<Dialog
				hasBusyIndicator={isBusy}
				trackingId="manage-spaces-and-apps-slider"
				variant="left-slider"
				onClose={onClose}
				backdrop={hasOtherDialogOpen ? "invisible" : undefined}
			>
				<Dialog.Header
					title={localize("_ManageSpaces")}
					onClose={onClose}
					hasBorder
				/>
				<Dialog.Body>
					<Form
						ref={formRef}
						attachable={formHandler}
						appItemsInfo={appItemsInfo}
						permissions={permissions}
						setAppItemsInfo={setAppItemsInfo}
						setRecentlyRemovedAppIds={setRecentlyRemovedAppIds}
						onAddAppAsTab={(targetGroupIndex) => setAddAppsSliderState({ isOpen: true, targetGroupIndex })}
						onCreateSharedSpace={createSharedSpaceHandler}
						onEditSpace={editSpaceHandler}
						appIdToScroll={appIdToScroll}
						onAppIdToScrollChange={setAppIdToScroll}
						onChangeIsBusy={setIsBusy}
					/>
				</Dialog.Body>
				<Dialog.Footer hasBorder>
					<Toolbar>
						<Toolbar.Button
							kind="primary"
							text={localize("_Save")}
							onClick={submitHandler}
							trackingId="save-button"
						/>

						<Toolbar.Button
							kind="secondary"
							text={localize("_Cancel")}
							onClick={onClose}
							trackingId="cancel-button"
						/>
					</Toolbar>
				</Dialog.Footer>
			</Dialog>

			{editSpaceDialog}
			{startCreateSpaceElement}
		</>
	);
};
GlobalNavigationManagement.displayName = "GlobalNavigationManagement";

export type GlobalNavigationManagementInitialData = Omit<GlobalNavigationManagementProps, "onClose" | "onAfterSaved">;

export function useGlobalNavigationManagementSidebar(): [
	element: ReactElement | null,
	open: (props: GlobalNavigationManagementInitialData) => Promise<boolean>
] {
	const [element, originalOpen] = useDataInputDialog<GlobalNavigationManagementInitialData, void>(
		(initialData, onClose, onSave) => (
			<GlobalNavigationManagement
				{...initialData}
				onClose={onClose}
				onAfterSaved={onSave}
			/>
		)
	);

	const open = useCallback(
		async (initialData: GlobalNavigationManagementInitialData): Promise<boolean> => {
			return (await originalOpen(initialData))
				.wasSuccessful;
		},
		[originalOpen]
	);

	return [
		element,
		open
	];
}

const itemValidationConfiguration = createValidationConfiguration<GlobalNavigationItemFormModel>();

itemValidationConfiguration
	.whenType(isGroupItem)
	.forObject("groupData")
	.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(32).activateOn("input").validateOn("input")
	);

itemValidationConfiguration
	.whenType(isGroupItem)
	.forObject("groupData")
	.forCollection("children")
	.forItems(
		itemValidationConfiguration,
		(x) => getClientId(x)
	);

const validationConfiguration = createValidationConfiguration<GlobalNavigationFormModel>();
validationConfiguration.forCollection("items")
	.forItems(
		itemValidationConfiguration,
		(x) => getClientId(x)
	);

const formInitialState: GlobalNavigationFormModel = {
	items: []
};

const initialPermissions: GlobalNavigationPermissionsModel = {
	canChangeGlobalNavigationOrder: false,
	canManageGlobalNavigation: false,
	canManageSharedSpaces: false,
	canManageSpacePermissions: false,
};

export type AppGlobalNavigationItemInfo = {
	name: string;
	image: AppImageDisplayInfo;
	color: AppColorViewModel;
};

function convertToGlobalNavigationItemFormModel (
	item: GlobalNavigationItemEditModel
): GlobalNavigationItemFormModel {
	switch (item.itemData.kind) {
		case GlobalNavigationItemKind.Space:
			return setClientId({
				id: item.id,
				kind: GlobalNavigationItemKind.Space,
				spaceData: {
					spaceId: item.itemData
						.spaceId
				},
				name: item.name,
				spaceKind: item.itemData.spaceKind,
				userInfo: item.itemData
					.userInfo
			});
		case GlobalNavigationItemKind.InstalledApp:
			return setClientId({
				id: item.id,
				kind: GlobalNavigationItemKind.InstalledApp,
				appData: {
					installedAppId: item.itemData.installedAppId,
				}
			});
		case GlobalNavigationItemKind.Group:
			return setClientId({
				id: item.id,
				kind: GlobalNavigationItemKind.Group,
				groupData: {
					name: item.name,
					children: (item.itemData.children)
						.map(convertToGlobalNavigationItemFormModel)
				}
			});
		default:
			never(item.itemData);
	}
};

function getInstalledAppIds(items: readonly GlobalNavigationItemFormModel[]): readonly number[] {
	const result: number[] = [];

	for (const item of items) {
		if (isAppItem(item)) {
			result.push(item.appData.installedAppId);
		}

		if (isGroupItem(item)) {
			result.push(...getInstalledAppIds(item.groupData.children));
		}
	}

	return result;
}

type AddAppsSliderState = {
	isOpen: false;
} | {
	isOpen: true;
	targetGroupIndex: number | null;
};

function getAppItemsData(
	items: readonly GlobalNavigationItemEditModel[]
): readonly { data: AppGlobalNavigationItemDataEditModel; name: string; }[] {
	const result: { data: AppGlobalNavigationItemDataEditModel; name: string; }[] = [];

	for (const item of items) {
		const {
			itemData,
			name
		} = item;

		if (itemData.kind === GlobalNavigationItemKind.InstalledApp) {
			result.push({ data: itemData, name });
		}

		if (itemData.kind === GlobalNavigationItemKind.Group) {
			result.push(
				...getAppItemsData(itemData.children)
			);
		}
	}

	return result;
}

function updateSpaceInfo(
	formData: GlobalNavigationFormModel,
	spaceId: number,
	name: string,
	userInfo: SpaceUserInfo
): GlobalNavigationFormModel {
	const processItem = (item: ObjectWithClientId<GlobalNavigationItemFormModel>): ObjectWithClientId<GlobalNavigationItemFormModel> => {
		switch (item.kind) {
			case GlobalNavigationItemKind.Group:
				return {
					...item,
					groupData: {
						...item.groupData,
						children: item.groupData.children
							.map(processItem)
					}
				};
			case GlobalNavigationItemKind.InstalledApp:
				return item;
			case GlobalNavigationItemKind.Space:
				if (item.spaceData.spaceId !== spaceId) {
					return item;
				}

				return {
					...item,
					name: name,
					userInfo: userInfo
				};
			default:
				never(item);
		}
	};

	return {
		...formData,
		items: formData.items
			.map(processItem)
	};
}

